import { createSlice } from '@reduxjs/toolkit'
import {
  fetchAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  fetchSingleAgent,
  approveAgent,
  disapproveAgent,
  deactivateAgentAccount,
  fetchAgentTeams,
  fetchAgentTeamMembers,
  fetchAgentContacts,
  fetchAgentReviews,
} from '../actions/agentActions.js'

const initialState = {
  agents: [],
  singleAgent: null,
  loading: false,
  error: null,
  deactivating: false,
  deactivateError: null,
  teams: [],
  teamsLoading: false,
  teamsError: null,
  teamMembers: [],
  teamMembersLoading: false,
  teamMembersError: null,
  contacts: [],
  contactsLoading: false,
  contactsError: null,
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,
}

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    resetAgentState: state => {
      state.agents = []
      state.loading = false
      state.error = null
    },
    getAgentRequest: state => {
      state.loading = true
      state.error = null
    },
    getAgentSuccess: (state, action) => {
      state.loading = false
      state.singleAgent = action.payload
    },
    getAgentFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    approveAgentRequest: state => {
      state.loading = true
      state.error = null
    },
    approveAgentSuccess: (state, action) => {
      state.loading = false
      state.singleAgent = action.payload
    },
    approveAgentFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    disapproveAgentRequest: state => {
      state.loading = true
      state.error = null
    },
    disapproveAgentSuccess: (state, action) => {
      state.loading = false
      state.singleAgent = action.payload
    },
    disapproveAgentFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    clearDeactivateError: state => {
      state.deactivateError = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAgents.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.agents = action.payload.data
        state.loading = false
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(fetchSingleAgent.pending, state => {
        state.loading = true
        state.singleAgent = null
        state.error = null
      })
      .addCase(fetchSingleAgent.fulfilled, (state, action) => {
        state.singleAgent = action.payload // Store single agent data
        state.loading = false
      })
      .addCase(fetchSingleAgent.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(approveAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(approveAgent.fulfilled, (state, action) => {
        state.agents = state.agents.map(agent => {
          if (agent._id === action.payload._id) {
            agent.verificationStatus = 'approved'
          }
          return agent
        })
        state.loading = false
      })
      .addCase(approveAgent.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(disapproveAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(disapproveAgent.fulfilled, (state, action) => {
        state.loading = false
        const updatedAgent = action.payload
        if (state.singleAgent?._id === updatedAgent._id) {
          state.singleAgent = updatedAgent
        }
        const index = state.agents.findIndex(agent => agent._id === updatedAgent._id)
        if (index !== -1) {
          state.agents[index] = updatedAgent
        }
      })
      .addCase(disapproveAgent.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(deactivateAgentAccount.pending, state => {
        state.deactivating = true
        state.deactivateError = null
      })
      .addCase(deactivateAgentAccount.fulfilled, (state, action) => {
        state.deactivating = false
        // The API returns { message, user }, so we access the user property
        const updatedAgent = action.payload.user
        if (state.singleAgent?._id === updatedAgent._id) {
          state.singleAgent = updatedAgent
        }
        const index = state.agents.findIndex(agent => agent._id === updatedAgent._id)
        if (index !== -1) {
          state.agents[index] = updatedAgent
        }
      })
      .addCase(deactivateAgentAccount.rejected, (state, action) => {
        state.deactivating = false
        state.deactivateError = action.payload
      })
      .addCase(createAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.agents.push(action.payload)
        state.loading = false
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(updateAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        const index = state.agents.findIndex(agent => agent._id === action.payload._id)
        state.agents[index] = action.payload
        state.loading = false
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(deleteAgent.fulfilled, state => {
        state.currentAgent = null
        state.loading = false
      })
      // Fetch agent teams
      .addCase(fetchAgentTeams.pending, state => {
        state.teamsLoading = true
        state.teamsError = null
      })
      .addCase(fetchAgentTeams.fulfilled, (state, action) => {
        state.teamsLoading = false
        state.teams = action.payload.teams || []
        state.teamsError = null
      })
      .addCase(fetchAgentTeams.rejected, (state, action) => {
        state.teamsLoading = false
        state.teamsError = action.payload
      })
      // Fetch agent team members
      .addCase(fetchAgentTeamMembers.pending, state => {
        state.teamMembersLoading = true
        state.teamMembersError = null
      })
      .addCase(fetchAgentTeamMembers.fulfilled, (state, action) => {
        state.teamMembersLoading = false
        state.teamMembers = action.payload.members || []
        state.teamMembersError = null
      })
      .addCase(fetchAgentTeamMembers.rejected, (state, action) => {
        state.teamMembersLoading = false
        state.teamMembersError = action.payload
      })
      // Fetch agent contacts
      .addCase(fetchAgentContacts.pending, state => {
        state.contactsLoading = true
        state.contactsError = null
      })
      .addCase(fetchAgentContacts.fulfilled, (state, action) => {
        state.contactsLoading = false
        state.contacts = action.payload.contacts || []
        state.contactsError = null
      })
      .addCase(fetchAgentContacts.rejected, (state, action) => {
        state.contactsLoading = false
        state.contactsError = action.payload
      })
      // Fetch agent reviews
      .addCase(fetchAgentReviews.pending, state => {
        state.reviewsLoading = true
        state.reviewsError = null
      })
      .addCase(fetchAgentReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false
        state.reviews = action.payload.reviews || []
        state.reviewsError = null
      })
      .addCase(fetchAgentReviews.rejected, (state, action) => {
        state.reviewsLoading = false
        state.reviewsError = action.payload
      })
  },
})

export const {
  resetAgentState,
  getAgentRequest,
  getAgentSuccess,
  getAgentFail,
  approveAgentRequest,
  approveAgentSuccess,
  approveAgentFail,
  disapproveAgentRequest,
  disapproveAgentSuccess,
  disapproveAgentFail,
  clearDeactivateError,
} = agentSlice.actions

export default agentSlice.reducer